import React, { useEffect } from 'react';
import ProjectPoster from './ProjectPoster';
import { paletteFor, colorForRevenueCategory } from '../data/showcaseProjects';

// ──────────────────────────────────────────────
// ProjectDetailModal
// Opens when a card on the showcase page is clicked.
// Two-column layout: poster (left) + rich content (right).
// Per-tier sections: funding progress + invest CTA, voting, or distribution + watch links.
// ──────────────────────────────────────────────

const fmt = (n) => `$${n.toLocaleString()}`;
const fmtK = (n) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n / 1000).toFixed(0)}K`);

export default function ProjectDetailModal({ project, tier, onClose, onInvest, onVote, currentVote }) {
  // Lock body scroll while open + handle Escape to dismiss
  useEffect(() => {
    if (!project) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [project, onClose]);

  if (!project) return null;

  // Detail data (extendedSynopsis, team, milestones, watchLinks, awards) now
  // lives on the project object itself — served from /api/showcase.
  const detail = project;
  const palette = paletteFor(project.genre);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(6, 10, 19, 0.92)',
        backdropFilter: 'blur(8px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '2.5rem 1rem',
        overflowY: 'auto',
        animation: 'pdm-fadeIn 200ms ease',
      }}
    >
      <style>{`
        @keyframes pdm-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pdm-slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pdm-card {
          animation: pdm-slideUp 350ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .pdm-watch-btn {
          padding: 0.7rem 1rem;
          background: rgba(6, 182, 212, 0.08);
          border: 1px solid rgba(6, 182, 212, 0.25);
          border-radius: 10px;
          color: #67E8F9;
          font-family: 'Instrument Sans', sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          transition: all 200ms ease;
        }
        .pdm-watch-btn:hover {
          background: rgba(6, 182, 212, 0.18);
          border-color: rgba(6, 182, 212, 0.5);
          transform: translateY(-1px);
        }
        @media (max-width: 880px) {
          .pdm-grid { grid-template-columns: 1fr !important; }
          .pdm-poster { max-width: 320px; margin: 0 auto; }
        }
      `}</style>

      <article
        className="pdm-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '1080px',
          width: '100%',
          background: 'linear-gradient(135deg, #1E293B 0%, #0B1120 100%)',
          border: '1px solid rgba(6, 182, 212, 0.2)',
          borderRadius: '20px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'rgba(15, 23, 42, 0.85)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#94A3B8',
            fontSize: '1.1rem',
            cursor: 'pointer',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#F1F5F9'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
        >
          ✕
        </button>

        <div className="pdm-grid" style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr',
          gap: '2rem',
          padding: '2rem',
        }}>
          {/* ─── Left column: poster ──────────────── */}
          <div className="pdm-poster">
            <div style={{
              aspectRatio: '2/3',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 14px 40px rgba(0, 0, 0, 0.55)',
            }}>
              <ProjectPoster project={project} variant="hero" />
            </div>

            {/* Tier-specific CTA below poster */}
            <TierCTA
              tier={tier}
              project={project}
              detail={detail}
              palette={palette}
              currentVote={currentVote}
              onInvest={onInvest}
              onVote={onVote}
            />
          </div>

          {/* ─── Right column: content ─────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Header */}
            <header>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.7rem',
                  color: palette.accent,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: '500',
                }}>
                  {project.genre}
                </span>
                <TierBadge tier={tier} project={project} />
              </div>
              <h2 style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '2.25rem',
                fontWeight: '800',
                color: '#F1F5F9',
                letterSpacing: '-0.03em',
                lineHeight: '1.05',
                margin: '0 0 0.5rem',
              }}>
                {project.title}
              </h2>
              <p style={{
                fontStyle: 'italic',
                color: '#94A3B8',
                fontSize: '1.0625rem',
                lineHeight: '1.5',
                margin: 0,
              }}>
                "{project.tagline}"
              </p>
            </header>

            {/* Synopsis */}
            <section>
              <SectionLabel>Synopsis</SectionLabel>
              {detail?.extendedSynopsis ? (
                detail.extendedSynopsis.map((para, i) => (
                  <p key={i} style={{
                    color: '#CBD5E1',
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                    margin: i === 0 ? '0 0 0.85rem' : '0 0 0.5rem',
                  }}>
                    {para}
                  </p>
                ))
              ) : (
                <p style={{ color: '#CBD5E1', fontSize: '0.95rem', lineHeight: '1.7', margin: 0 }}>
                  {project.logline}
                </p>
              )}
            </section>

            {/* Spec strip */}
            {(detail?.runtime || detail?.format) && (
              <div style={{
                display: 'flex',
                gap: '2rem',
                flexWrap: 'wrap',
                paddingTop: '0.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                {detail.runtime && (
                  <SpecItem label="Runtime" value={detail.runtime} />
                )}
                {detail.format && (
                  <SpecItem label="Format" value={detail.format} />
                )}
                <SpecItem label="Director" value={project.director} />
              </div>
            )}

            {/* Team */}
            {detail?.team && (
              <section>
                <SectionLabel>Team</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.65rem' }}>
                  {detail.team.map((member) => (
                    <div key={member.name} style={{
                      padding: '0.7rem 0.85rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.04)',
                      borderRadius: '10px',
                    }}>
                      <div style={{ color: '#F1F5F9', fontSize: '0.875rem', fontWeight: '600' }}>
                        {member.name}
                      </div>
                      <div style={{ color: palette.accent, fontSize: '0.7rem', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em', textTransform: 'uppercase', margin: '0.15rem 0' }}>
                        {member.role}
                      </div>
                      <div style={{ color: '#8892A4', fontSize: '0.75rem', lineHeight: '1.4' }}>
                        {member.credit}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tier-specific detail section */}
            <TierDetailSection tier={tier} project={project} detail={detail} />
          </div>
        </div>
      </article>
    </div>
  );
}

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <h4 style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.7rem',
      color: '#64748B',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      fontWeight: '500',
      margin: '0 0 0.6rem',
    }}>
      {children}
    </h4>
  );
}

function SpecItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.65rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.15rem' }}>
        {label}
      </div>
      <div style={{ color: '#E2E8F0', fontSize: '0.875rem', fontWeight: '500' }}>
        {value}
      </div>
    </div>
  );
}

function TierBadge({ tier, project }) {
  if (tier === 'funding') {
    return (
      <span style={{
        fontSize: '0.7rem',
        padding: '0.2rem 0.6rem',
        background: 'rgba(132, 204, 22, 0.12)',
        border: '1px solid rgba(132, 204, 22, 0.3)',
        borderRadius: '4px',
        color: '#A3E635',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        ★ NOW FUNDING
      </span>
    );
  }
  if (tier === 'voting') {
    return (
      <span style={{
        fontSize: '0.7rem',
        padding: '0.2rem 0.6rem',
        background: 'rgba(34, 211, 238, 0.12)',
        border: '1px solid rgba(34, 211, 238, 0.3)',
        borderRadius: '4px',
        color: '#22D3EE',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        ↑ COMMUNITY VOTING
      </span>
    );
  }
  // distribution
  return (
    <span style={{
      fontSize: '0.7rem',
      padding: '0.2rem 0.6rem',
      background: 'rgba(168, 85, 247, 0.12)',
      border: '1px solid rgba(168, 85, 247, 0.3)',
      borderRadius: '4px',
      color: '#C4B5FD',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      ● {project.release?.status?.toUpperCase() || 'RELEASED'}
    </span>
  );
}

// ──────────────────────────────────────────────
// Tier-specific CTA (under the poster)
// ──────────────────────────────────────────────
function TierCTA({ tier, project, detail, palette, currentVote, onInvest, onVote }) {
  if (tier === 'funding') {
    const { funding } = project;
    const pct = Math.min(100, Math.round((funding.raised / funding.goal) * 100));
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.5rem', fontWeight: '700', color: '#84CC16' }}>
            {fmt(funding.raised)}
          </span>
          <span style={{ color: '#64748B', fontSize: '0.8125rem' }}>
            of {fmt(funding.goal)}
          </span>
        </div>
        <div style={{ height: '8px', background: 'rgba(6, 182, 212, 0.08)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #06B6D4, #84CC16)',
            borderRadius: '4px',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.45rem', fontSize: '0.7rem', color: '#94A3B8', fontFamily: "'JetBrains Mono', monospace" }}>
          <span>{pct}% funded</span>
          <span>{funding.investors} investors</span>
          <span>{funding.daysLeft} days left</span>
        </div>
        <button
          onClick={onInvest}
          style={{
            marginTop: '1rem',
            width: '100%',
            padding: '0.95rem',
            background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
            color: '#0F172A',
            border: 'none',
            borderRadius: '10px',
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: '700',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'transform 200ms ease, box-shadow 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(6, 182, 212, 0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          Invest from ${funding.minInvestment}
        </button>
        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.7rem', color: '#64748B' }}>
          Reg CF · No wallet required
        </div>
      </div>
    );
  }

  if (tier === 'voting') {
    const { voting } = project;
    const adjusted = voting.current + (currentVote === 'up' ? 1 : 0);
    const pct = Math.min(100, Math.round((adjusted / voting.threshold) * 100));
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#94A3B8', fontFamily: "'JetBrains Mono', monospace", marginBottom: '0.35rem' }}>
          <span>{adjusted.toLocaleString()} of {voting.threshold.toLocaleString()}</span>
          <span style={{ color: '#84CC16' }}>{pct}%</span>
        </div>
        <div style={{ height: '6px', background: 'rgba(132, 204, 22, 0.08)', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #84CC16, #A3E635)',
            borderRadius: '3px',
          }} />
        </div>
        <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.35rem', textAlign: 'center' }}>
          {pct < 100
            ? `${(voting.threshold - adjusted).toLocaleString()} votes to greenlight`
            : 'Greenlit — entering compliance review'}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            onClick={() => onVote('up')}
            disabled={currentVote === 'up'}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: currentVote === 'up' ? 'rgba(132, 204, 22, 0.25)' : 'rgba(132, 204, 22, 0.08)',
              border: `1px solid rgba(132, 204, 22, ${currentVote === 'up' ? 0.5 : 0.25})`,
              borderRadius: '10px',
              color: currentVote === 'up' ? '#A3E635' : '#84CC16',
              fontFamily: "'Instrument Sans', sans-serif",
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: currentVote === 'up' ? 'default' : 'pointer',
            }}
          >
            ▲ Greenlight
          </button>
          <button
            onClick={() => onVote('down')}
            disabled={currentVote === 'down'}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: currentVote === 'down' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.06)',
              border: `1px solid rgba(148, 163, 184, ${currentVote === 'down' ? 0.4 : 0.18})`,
              borderRadius: '10px',
              color: currentVote === 'down' ? '#CBD5E1' : '#94A3B8',
              fontFamily: "'Instrument Sans', sans-serif",
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: currentVote === 'down' ? 'default' : 'pointer',
            }}
          >
            ▼ Pass
          </button>
        </div>
      </div>
    );
  }

  // distribution
  if (detail?.watchLinks) {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.5rem', textAlign: 'center' }}>
          Watch on
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {detail.watchLinks.map((w) => (
            <a
              key={w.channel}
              href={w.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pdm-watch-btn"
            >
              ▶ {w.channel}
            </a>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ──────────────────────────────────────────────
// Tier-specific detail section (in right column)
// ──────────────────────────────────────────────
function TierDetailSection({ tier, project, detail }) {
  if (tier === 'funding') {
    return (
      <>
        {detail?.milestones && (
          <section>
            <SectionLabel>Funding milestones</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {detail.milestones.map((m) => (
                <div key={m.label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.6rem 0.85rem',
                  background: m.reached ? 'rgba(132, 204, 22, 0.06)' : 'rgba(15, 23, 42, 0.4)',
                  border: `1px solid ${m.reached ? 'rgba(132, 204, 22, 0.18)' : 'rgba(255, 255, 255, 0.04)'}`,
                  borderRadius: '8px',
                }}>
                  <span style={{
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: m.reached ? '#84CC16' : 'transparent',
                    border: m.reached ? 'none' : '2px solid #475569',
                    color: '#0F172A', fontSize: '0.7rem', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {m.reached ? '✓' : ''}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.875rem', color: m.reached ? '#E2E8F0' : '#94A3B8' }}>
                    {m.label} <span style={{ color: '#64748B', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', marginLeft: '0.4rem' }}>{fmt(m.target)}</span>
                  </span>
                  {m.reached && m.date && (
                    <span style={{ fontSize: '0.7rem', color: '#84CC16', fontFamily: "'JetBrains Mono', monospace" }}>
                      {m.date}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </>
    );
  }

  if (tier === 'distribution') {
    const { release } = project;
    const roi = (release.revenueTotal / release.totalRaised).toFixed(2);
    return (
      <>
        {/* Distribution channels */}
        <section>
          <SectionLabel>Distribution</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {release.channels.map((ch) => (
              <span key={ch} style={{
                padding: '0.35rem 0.85rem',
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.2)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                color: '#67E8F9',
                fontWeight: '500',
              }}>
                {ch}
              </span>
            ))}
          </div>
        </section>

        {/* Financial summary */}
        <section>
          <SectionLabel>Financial summary</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <FinStat label="Raised" value={fmtK(release.totalRaised)} color="#22D3EE" />
            <FinStat label="Revenue" value={fmtK(release.revenueTotal)} color="#A3E635" />
            <FinStat label="Multiple" value={`${roi}x`} color="#F472B6" />
          </div>
        </section>

        {/* Revenue breakdown table */}
        <section>
          <SectionLabel>Revenue breakdown</SectionLabel>
          <div style={{
            display: 'flex',
            height: '14px',
            borderRadius: '7px',
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.03)',
            marginBottom: '0.75rem',
          }}>
            {release.revenueBreakdown.map((seg) => {
              const pct = (seg.amount / release.revenueTotal) * 100;
              const color = colorForRevenueCategory(seg.label);
              return (
                <div key={seg.label} style={{ width: `${pct}%`, background: color }} title={`${seg.label}: ${fmt(seg.amount)} (${pct.toFixed(1)}%)`} />
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {release.revenueBreakdown.map((seg) => {
              const pct = ((seg.amount / release.revenueTotal) * 100).toFixed(1);
              const color = colorForRevenueCategory(seg.label);
              return (
                <div key={seg.label} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  fontSize: '0.875rem',
                  paddingBottom: '0.4rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, flexShrink: 0 }} />
                  <span style={{ color: '#CBD5E1', flex: 1 }}>{seg.label}</span>
                  <span style={{ color: '#94A3B8', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>{pct}%</span>
                  <span style={{ color: '#F1F5F9', fontFamily: "'JetBrains Mono', monospace", fontWeight: '600', minWidth: '90px', textAlign: 'right' }}>
                    {fmt(seg.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Awards */}
        {detail?.awards && (
          <section>
            <SectionLabel>Festival & awards</SectionLabel>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#CBD5E1', fontSize: '0.875rem', lineHeight: '1.65' }}>
              {detail.awards.map((a) => <li key={a}>{a}</li>)}
            </ul>
          </section>
        )}
      </>
    );
  }

  return null; // voting tier — already covered by CTA
}

function FinStat({ label, value, color }) {
  return (
    <div style={{
      padding: '0.85rem',
      background: 'rgba(15, 23, 42, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.04)',
      borderRadius: '10px',
      textAlign: 'center',
    }}>
      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.25rem', fontWeight: '700', color }}>
        {value}
      </div>
      <div style={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '0.2rem' }}>
        {label}
      </div>
    </div>
  );
}
